/**
 * Frontend half of the whitelist/permission boundary (ADR-UI-003 §3/§7). The
 * backend remains the authoritative half — this only hides a tab body the
 * caller cannot discover, reactively, without a layout reload.
 */
import type { ReactNode } from "react";
import { observer } from "mobx-react-lite";
import type { PermissionCode } from "../../app/generated/shell-permission-codes";
import { EmptyState } from "../../ui/empty-state";
import { workspaceText } from "../i18n";
import type { WorkspaceLocale } from "../i18n";

export interface WorkspacePermissionBoundaryProps {
  readonly required: readonly PermissionCode[];
  readonly canDiscover: (required: readonly PermissionCode[]) => boolean;
  readonly locale?: WorkspaceLocale | undefined;
  readonly children: ReactNode;
}

/** `observer`-wrapped so a session permission change re-renders this reactively, not only on next layout load. */
export const WorkspacePermissionBoundary = observer(function WorkspacePermissionBoundary({
  canDiscover,
  children,
  locale = "en",
  required,
}: WorkspacePermissionBoundaryProps): ReactNode {
  if (!canDiscover(required)) {
    return <EmptyState title={workspaceText(locale, "workspaceForbidden")} />;
  }
  return children;
});
